/** URL for the page to request a quote on a subscription */
export const REQUEST_A_QUOTE_URL = "https://www.holobuilder.com/pricing";

/**
 * These are some of the subscription labels that companies have, they provide some special functionality
 * within the workspace but they do not represent a full subscription plan.
 * Therefore, we want to ignore them when determining the main subscription of a company.
 * This list is not exhaustive since there are older subscriptions that we no longer know of.
 */
export const IGNORED_SUBSCRIPTION_LABELS = ["Default Plan - Dashboard", "Default Plan - Minimal", "Add 3000 Tokens (Small)", "Add 6000 Tokens (Medium)", "Add 9000 Tokens (Large)", "Add 15000 Tokens (Large)", "Add Custom Tokens", "Add Credits", "Dashboard", "Premium Analytics", "GPS Features", "Project SnapShots", "Point Cloud Management", "3D Model Support", "Floorplan Generator", "Project Evaluation", "360 Download", "Insights Teaser", "VideoMode", "SendTo", "'Sphere Dashboard' app access (replaces HoloBuilder Dashboard)", "Face Blurring V2", "Object Detection", "Photogrammetry", "Videomode-Pointclouds", "Projects Export", "Feature Flag for Support of Focus, Orbis Staging Area Workflows in XG (Beta)"];

/** A set of all ignored subscription labels in lowercase */
export const ALL_IGNORED_LABELS_LOWER_CASE = new Set(IGNORED_SUBSCRIPTION_LABELS.map(label => label.toLowerCase()));

/** This is a special subscription that is normally assigned to projects */
export const VALUE_PRICING_MODEL_LABEL = "Value Pricing Model";
export const LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS = ["Plan FM", "Education", "Enterprise Features", VALUE_PRICING_MODEL_LABEL];

/** A set of all legacy Holobuilder subscription labels in lowercase */
export const LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS_LOWER_CASE = new Set(LEGACY_HOLOBUILDER_SUBSCRIPTION_LABELS.map(label => label.toLowerCase()));
export const LEGACY_SPHERE_SUBSCRIPTION_LABELS = ["WebShare Base", "WebShare Pro", "Sphere Professional", "Sphere Enterprise"];

/** A set of all legacy Sphere subscription labels in lowercase */
export const LEGACY_SPHERE_SUBSCRIPTION_LABELS_LOWER_CASE = new Set(LEGACY_SPHERE_SUBSCRIPTION_LABELS.map(label => label.toLowerCase()));

/** Whitelist to filter the quotas */
export const WHITELISTED_IDENTIFIERS = ["project_area", "project_count", "user_role", "storage"];

/** Unit label for each limit type */
export const LIMIT_UNIT_LABEL = {
  ["project_area"]: "Sq ft",
  ["project_count"]: "projects",
  ["user_role"]: "members"
};

/** List of limit types that are taken into account for showing exceeded limit warnings  */
export const LIMIT_TYPE_WARNING_WHITELIST = ["user_role", "storage", "project_count", "project_area"];

/** Name when we cannot determine if a subscription is XG, legacy HB or legacy Sphere */
export const DEFAULT_SUBSCRIPTION_NAME_NO_SUBSCRIPTION = "No Active Subscription";

/** Name for the Legacy HoloBuilder subscription */
export const DEFAULT_SUBSCRIPTION_NAME_LEGACY_HOLOBUILDER = "HoloBuilder Legacy";

/** Name for the Legacy WebShare (Sphere 1) subscription */
export const DEFAULT_SUBSCRIPTION_NAME_LEGACY_WEB_SHARE = "WebShare Legacy";

/**
 * Since there is currently no reliable way to identify VPM subscriptions from the subscription data,
 * we maintain a list of known subscription IDs that correspond to VPM subscriptions.
 * To make it harder in the client to identify the company id, we only store the first 2 characters
 * and the last 4 characters of the subscription ID.
 * This should be unique enough to identify the subscription, but not easily reversible to get the full ID.
 * TODO: Once the subscriptions expire, remove the ids from the list until this workaround is no longer needed.
 */
export const VPM_COMPANY_SUB_IDS_SET = new Set([/** Prod COM, expires 07/12/2026  */
"58-8624", /** Prod COM, expires 02/10/2026  */
"13-1f90", /** Prod COM, expires 28/02/2026  */
"29-d94e", /** Prod EU, expires 02/06/2026  */
"23-caa7", /** Prod COM expires 09/12/2027 */
"48-9216", /** Dev COM, 0532b080-112b-4811-bc22-644df68fc487 StellarPlansVPM  */
"05-c487"]);

/**
 * Query options used whenever the subscriptions of a workspace are fetched. Subscriptions can have
 * the following statuses:
 * Active - between start and end date
 * Expired - end date is in the past
 * Invalid - between start and end date but at least one limit is over the limit and
 * thus the subscription is not providing the Features it contains.
 * Upcoming - start date is in the future, e.g. a renewal that takes over once the current term ends.
 */
export const COMPANY_SUBSCRIPTIONS_QUERY_OPTIONS = {
  status: ["ACTIVE", "EXPIRED", "INVALID", "UPCOMING"]
};